import { createContext, useContext } from 'react';
import type { CoverBankBackground, GetCoverEditorResponse } from '../../redux/api/fymAPI.types';
import type { CoverEditorState } from './doc/coverEditorReducer';
import type { CoverEditorDispatch } from './doc/useCoverEditorDoc';

/** The tools of the left rail (design cover-editor b1). */
export const COVER_TOOLS = ['picture', 'text', 'effects', 'layers'] as const;
export type CoverTool = (typeof COVER_TOOLS)[number];

/** `song`: the editor of one song of an account. `public`: the public tool, with no account. */
export type CoverEditorMode = 'song' | 'public';

/** The links of the public tool to an account (design cover-editor g1, g2). */
export interface CoverPublicTool {
  /** Opens the sign-up; the design goes with the visitor. */
  onSignUp: () => void;
  /** The plans page of music24.com. */
  plansHref: string;
  /** True inside the frame of the website: a link opens in the page around the frame. */
  isEmbed: boolean;
}

export interface CoverEditorContextValue {
  /**
   * Where the editor runs. In `public` mode there is no account and no song: `accountId` is 0,
   * `catalogItemUuid` and `designUuid` are empty, and `data` is a made-up answer of a plan with no
   * AI (`publicMode/publicCoverData.ts`). A part that calls an account route must check the mode.
   */
  mode: CoverEditorMode;
  accountId: number;
  catalogItemUuid: string;
  /** The open version. */
  designUuid: string;
  /** The answer of `GET .../cover-editor/:uuid`: the song, the versions, the AI pictures, the month count. */
  data: GetCoverEditorResponse;
  /** The published bank pictures. */
  backgrounds: CoverBankBackground[];
  state: CoverEditorState;
  dispatch: CoverEditorDispatch;
  canUndo: boolean;
  canRedo: boolean;
  /** True for the example artist: every control that changes something is off. */
  readOnly: boolean;
  /** Only in `public` mode: the ways to an account. A click keeps the design for the sign-in first. */
  publicTool?: CoverPublicTool;
  tool: CoverTool;
  setTool: (tool: CoverTool) => void;
}

export const CoverEditorContext = createContext<CoverEditorContextValue | null>(null);

/** The open editor. It throws outside the editor: a part of the editor never renders alone. */
export function useCoverEditor(): CoverEditorContextValue {
  const value = useContext(CoverEditorContext);
  if (value === null) {
    throw new Error('useCoverEditor: no cover editor above this component');
  }
  return value;
}
